'use client';

import { CacheKey } from '@eliadmin/core/src/api/constants';
import { addProductToFavorites } from '@eliadmin/core/src/api/customers/customers-api';
import { fetchSlugModel } from '@eliadmin/core/src/api/slugs/fetch-slug-model';
import { Button, type ButtonProps } from '@eliadmin/core/src/components/ui/button';
import { Spinner } from '@eliadmin/core/src/components/ui/spinner';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { checkIfInLocalStorage } from '@eliadmin/core/src/utils/favorite-utils';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRouter } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';
import { FiHeart } from 'react-icons/fi';

type AddToFavoritesButtonProps = {
  slug: string;
  className?: string;
  isFavorite?: boolean;
  productId?: number;
  showText?: boolean;
  buttonVariant?: ButtonProps['variant'];
  buttonSize?: ButtonProps['size'];
  buttonClassName?: string;
};

export function AddToFavoritesButton(props: AddToFavoritesButtonProps) {
  const hasCustomer = useCustomerStore(state => !!state.me?.authToken);
  const startedAsGuest = useRef(!hasCustomer);
  const [activated, setActivated] = useState(hasCustomer);
  useEffect(() => {
    if (hasCustomer) setActivated(true);
  }, [hasCustomer]);
  // Do not unmount an authenticated mutation owner during logout. A guest
  // browsing a listing never needs a disabled query/mutation on every card.
  return hasCustomer || activated ? (
    <CustomerFavoritesButton {...props} refetchOnMount={startedAsGuest.current} />
  ) : (
    <GuestFavoritesButton {...props} />
  );
}

function GuestFavoritesButton(props: AddToFavoritesButtonProps) {
  const enabled = useGlobalStore(
    state =>
      state.templateSettings.product_teaser?.enable_add_to_favorite_when_not_logged_in?.enabled,
  );
  const router = useRouter();
  const [isFavorite, setFavorite] = useState(!!props.isFavorite);
  useEffect(() => {
    const sync = () =>
      setFavorite(
        enabled && props.productId != null
          ? !!checkIfInLocalStorage(props.productId)
          : !!props.isFavorite,
      );
    sync();
    window.addEventListener('storage', sync);
    return () => window.removeEventListener('storage', sync);
  }, [enabled, props.productId, props.isFavorite]);
  return (
    <FavoritesButtonView
      {...props}
      isFavorite={isFavorite}
      onClick={event => {
        event.stopPropagation();
        if (!enabled) {
          router.push(ROUTES.SIGN_IN());
          return;
        }
        if (props.productId == null) return;
        const stored = JSON.parse(localStorage.getItem('favoriteProducts') || '[]') as number[];
        const favorites = stored.includes(props.productId)
          ? stored.filter(id => id !== props.productId)
          : [...stored, props.productId];
        localStorage.setItem('favoriteProducts', JSON.stringify(favorites));
        setFavorite(favorites.includes(props.productId));
        window.dispatchEvent(new Event('storage'));
      }}
    />
  );
}

function CustomerFavoritesButton(props: AddToFavoritesButtonProps & { refetchOnMount: boolean }) {
  const authToken = useCustomerStore(state => state.me?.authToken);
  const locale = useLocaleStore(s => s.currentLocale);
  const queryClient = useQueryClient();
  const customerId = useCustomerStore(state => state.me?.customer.id ?? null);
  const owner = JSON.stringify([props.slug, locale, customerId, authToken ?? null]);
  const sequence = useRef(0);
  const [optimistic, setOptimistic] = useState<{
    owner: string;
    sequence: number;
    isFavorite: boolean;
  } | null>(null);

  const { data: product } = useQuery({
    queryKey: [CacheKey.PRODUCT_DETAIL, props.slug, locale, customerId],
    queryFn: () =>
      fetchSlugModel({
        model: 'App\\Models\\Product',
        slug: props.slug,
        locale,
        authToken: authToken,
      }),
    enabled: !!authToken && !!props.slug,
    refetchOnWindowFocus: false,
    // Settled writes can invalidate an origin while its route is unmounted.
    // Reconcile only that stale origin on return; fresh SSR seeds stay untouched.
    refetchOnMount: query => props.refetchOnMount || query.state.isInvalidated,
    refetchInterval: false,
    refetchIntervalInBackground: false,
  });

  const { mutate, isPending, variables } = useMutation({
    mutationFn: (mutation: {
      request: Parameters<typeof addProductToFavorites>[0];
      owner: string;
      customerId: number | null;
      sequence: number;
      isFavorite: boolean;
    }) => addProductToFavorites(mutation.request),
    onSettled: (_data, error, mutation) => {
      // Query observer options can change while the request is pending. Only
      // its immutable request variables identify the cache this result owns.
      const { request } = mutation;
      const queryKey = [
        CacheKey.PRODUCT_DETAIL,
        request.productSlug,
        request.locale,
        mutation.customerId,
      ];
      if (!error) {
        queryClient.setQueryData(queryKey, (cached: typeof product) => ({
          ...cached,
          data: {
            ...cached?.data,
            is_favorite: mutation.isFavorite,
          },
        }));
      }
      // The toggle API has no typed authoritative favorite value. Re-read the
      // exact origin even without observers: response order is not toggle order.
      queryClient.invalidateQueries({ queryKey, exact: true, refetchType: 'all' });
      queryClient.invalidateQueries({
        queryKey: [CacheKey.FAVORITE_PRODUCTS, request.locale, mutation.customerId],
      });
      setOptimistic(current => (current?.sequence === mutation.sequence ? null : current));
    },
  });

  const isFavorite =
    optimistic?.owner === owner
      ? optimistic.isFavorite
      : product?.data?.is_favorite ?? !!props.isFavorite;

  function handleAddToFavorites(e: React.MouseEvent<HTMLButtonElement>) {
    e.stopPropagation();
    if (!authToken) return;
    const next = { owner, sequence: ++sequence.current, isFavorite: !isFavorite };
    setOptimistic(next);
    mutate({ ...next, customerId, request: { productSlug: props.slug, authToken, locale } });
  }

  // Keep this mutation owner mounted, but restore the same subscribed guest
  // presentation after logout (including cross-instance storage and cleanup).
  if (!authToken) return <GuestFavoritesButton {...props} />;

  return (
    <FavoritesButtonView
      {...props}
      isFavorite={isFavorite}
      isMutating={isPending && variables?.owner === owner}
      productName={product?.data?.name}
      onClick={handleAddToFavorites}
    />
  );
}

function FavoritesButtonView(
  props: AddToFavoritesButtonProps & {
    isMutating?: boolean;
    productName?: string;
    onClick: React.MouseEventHandler<HTMLButtonElement>;
  },
) {
  const { t } = useTranslations();
  const { isFavorite, isMutating } = props;
  const action = isFavorite ? t('text532') : t('text531');
  const buttonLabel = props.showText ? action : `${action} ${props.productName ?? props.slug}`;
  return (
    <Button
      variant={props.buttonVariant ?? 'outline'}
      size={props.buttonSize ?? 'icon-sm'}
      onClick={props.onClick}
      className={cn('px-3', props.buttonClassName)}
      aria-label={buttonLabel}
      aria-pressed={!!isFavorite}
    >
      {isMutating ? (
        <Spinner className="h-6 w-6" />
      ) : (
        <div className="flex flex-row">
          <FiHeart
            className={cn(
              'h-4 w-4 cursor-pointer transition-colors duration-200 ease-in-out hover:text-muted-foreground sm:h-[18px]  sm:w-[18px]',
              'text-foreground',
              isFavorite ? 'fill-destructive stroke-destructive' : 'fill-none',
              props.className,
            )}
            aria-hidden="true"
            strokeWidth={1.5}
          />
          {props.showText && (
            <span className="link ml-2 underline">{isFavorite ? t('text532') : t('text531')}</span>
          )}
        </div>
      )}
    </Button>
  );
}
